(() => {
  const key = 'woofin.saved.v1';
  if (window.woofinSavedInitialized) return;
  window.woofinSavedInitialized = true;
  const config = JSON.parse(document.querySelector('[data-woofin-session]')?.textContent || '{}');
  const syncing = config.enabled === true;
  if (syncing) document.querySelectorAll('[data-woofin-signin]').forEach(link => { link.hidden = false; });
  let accountItems = [];
  let accountReady = false;
  let accountToken = '';
  let accountPromise;
  let lastError = '';
  const pendingKey = 'woofin.pending-save.v1';
  const filters = new WeakMap();
  const renderVersions = new WeakMap();
  const cards = new Map();

  function readLegacy() {
    try {
      const items = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(items) ? items.filter(item =>
        item && typeof item.id === 'string' && typeof item.title === 'string' &&
        typeof item.url === 'string' && /^\/(products|blogs)\//.test(item.url) &&
        ['product', 'article'].includes(item.kind)
      ) : [];
    } catch {
      return [];
    }
  }

  function read() { return syncing ? accountItems : readLegacy(); }
  function status(message) {
    lastError = message;
    let target = document.querySelector('[data-woofin-status]');
    if (!target) {
      target = document.createElement('p');
      target.dataset.woofinStatus = '';
      target.setAttribute('role', 'status');
      (document.querySelector('[data-woofin-save]')?.parentElement || document.querySelector('[data-woofin-saved]'))?.append(target);
    }
    if (target) { target.textContent = message; target.hidden = !message; }
  }
  async function accountRequest(input) {
    const response = await fetch(config.endpoint, {
      method: input ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      signal: AbortSignal.timeout(15000),
      ...(input ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...input, token: accountToken }) } : {})
    });
    let data;
    try { data = await response.json(); } catch { throw Error('Account syncing is temporarily unavailable. Please try again.'); }
    if (!response.ok) {
      if (response.status === 401) accountReady = false;
      throw Error(data.error || 'Could not update saved items. Please try again.');
    }
    if (!data.authenticated) { accountItems = []; accountToken = ''; accountReady = false; return false; }
    if (!Array.isArray(data.items) || typeof data.token !== 'string') throw Error('Could not load saved items.');
    accountItems = data.items;
    accountToken = data.token;
    accountReady = true;
    return true;
  }
  function loginFor(item) {
    const returnTo = location.pathname + location.search;
    try { sessionStorage.setItem(pendingKey, JSON.stringify({ item, returnTo, created: Date.now() })); }
    catch { status('Allow browser storage so we can finish saving after sign-in.'); return; }
    const login = new URL('/customer_authentication/login', location.origin);
    login.searchParams.set('return_to', returnTo);
    location.assign(login.pathname + login.search);
  }
  async function loadAccount() {
    if (!syncing || config.signedIn !== true) { render(); return; }
    if (accountPromise) return accountPromise;
    accountPromise = (async () => {
      try {
        if (!await accountRequest()) { status('Sign in to view your saved items.'); render(); return; }
        // Import the old browser-only prototype once, never cache account data in localStorage.
        const legacy = readLegacy();
        for (let start = 0; start < legacy.length; start += 100) {
          await accountRequest({ action: 'merge', items: legacy.slice(start, start + 100) });
        }
        if (legacy.length) localStorage.removeItem(key);
        let pending;
        try { pending = JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); } catch { sessionStorage.removeItem(pendingKey); }
        if (pending && Date.now() - pending.created >= 0 && Date.now() - pending.created < 1800000 && pending.returnTo === location.pathname + location.search) {
          if (await accountRequest({ action: 'save', item: pending.item })) {
            sessionStorage.removeItem(pendingKey);
            status('Saved to your WOOFIN account.');
          }
        } else if (pending && Date.now() - pending.created >= 1800000) sessionStorage.removeItem(pendingKey);
        if (!pending) status('');
      } catch (error) { status(error.message); }
      finally { render(); }
    })();
    try { await accountPromise; } finally { accountPromise = null; }
  }

  const excerptRequests = new Map();

  function articleExcerpt(item) {
    if (typeof item.excerpt === 'string' && item.excerpt.trim()) {
      return Promise.resolve(item.excerpt);
    }
    if (!excerptRequests.has(item.url)) {
      excerptRequests.set(item.url, fetch(item.url, { credentials: 'same-origin' })
        .then(response => response.ok ? response.text() : '')
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('.article__content');
          return (content?.textContent || '').trim().split(/\s+/).slice(0, 25).join(' ');
        })
        .catch(() => ''));
    }
    return excerptRequests.get(item.url);
  }

  function bookmarkIcon(kind) {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.7');
    icon.setAttribute('aria-hidden', 'true');
    const ribbon = document.createElementNS(namespace, 'path');
    ribbon.setAttribute('d', kind === 'product'
      ? 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z'
      : 'M6 3h12v18l-6-4-6 4V3Z');
    const slash = document.createElementNS(namespace, 'path');
    slash.setAttribute('d', 'M3 3l18 18');
    slash.classList.add('my-woofin__slash');
    icon.append(ribbon, slash);
    return icon;
  }

  async function card(item) {
    const wrapper = document.createElement('article');
    wrapper.className = 'my-woofin__card my-woofin__card--' + item.kind;
    const media = document.createElement('div');
    media.className = 'my-woofin__media';
    const imageLink = document.createElement('a');
    imageLink.href = item.url;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    if (typeof item.image === 'string' && /^(https:)?\/\//.test(item.image)) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      imageLink.append(image);
    }
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'my-woofin__remove';
    remove.title = 'Remove from saved';
    remove.setAttribute('aria-label', 'Remove ' + item.title + ' from saved');
    remove.dataset.woofinRemove = '';
    remove.dataset.kind = item.kind;
    remove.dataset.id = item.id;
    remove.append(bookmarkIcon(item.kind));
    media.append(imageLink, remove);
    const title = document.createElement('h3');
    title.className = 'my-woofin__title heading-small';
    const titleLink = document.createElement('a');
    titleLink.href = item.url;
    titleLink.textContent = item.title;
    title.append(titleLink);
    wrapper.append(media, title);
    if (item.kind === 'article') {
      const excerpt = document.createElement('p');
      excerpt.className = 'my-woofin__excerpt';
      wrapper.append(excerpt);
      const text = await articleExcerpt(item);
      excerpt.textContent = text;
      excerpt.hidden = !text;
    }
    if (item.kind === 'product') {
      const url = new URL(item.url, location.origin);
      url.searchParams.set('view', 'woofin-saved-card');
      await fetch(url.pathname + url.search, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('Product unavailable');
          return response.text();
        })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const productCard = doc.querySelector('.product-item');
          if (!productCard) throw new Error('Product card unavailable');
          productCard.querySelectorAll('[data-aos]').forEach(element => element.removeAttribute('data-aos'));
          const content = document.importNode(productCard, true);
          content.querySelectorAll('img').forEach(image => {
            const finishLoading = () => {
              image.classList.remove('is-loading');
              image.closest('.lazy-image')?.classList.remove('is-loading');
            };
            image.addEventListener('load', finishLoading, { once: true });
            image.addEventListener('error', finishLoading, { once: true });
            if (image.complete) finishLoading();
          });
          wrapper.replaceChildren(content, remove);
        })
        .catch(() => {
          const status = document.createElement('p');
          status.className = 'my-woofin__excerpt';
          status.textContent = 'View product for current price';
          wrapper.append(status);
        });
    }
    return wrapper;
  }

  function savedCard(item) {
    const signature = JSON.stringify(item);
    if (!cards.has(signature)) cards.set(signature, card(item));
    return cards.get(signature);
  }

  async function renderSection(section, items) {
    const version = (renderVersions.get(section) || 0) + 1;
    renderVersions.set(section, version);
    const filter = filters.get(section) || 'product';
    for (const button of section.querySelectorAll('[data-filter]')) {
      button.setAttribute('aria-pressed', String(button.dataset.filter === filter));
    }
    const list = section.querySelector('[data-items]');
    const empty = section.querySelector('[data-empty]');
    list.setAttribute('aria-busy', 'true');
    const hasCards = list.children.length > 0;
    if (!hasCards && items.length) {
      empty.hidden = false;
      empty.textContent = 'Loading saved items…';
    }
    const nodes = await Promise.all(items.map(savedCard));
    if (renderVersions.get(section) !== version || !section.isConnected) return;
    nodes.forEach((node, index) => {
      node.hidden = items[index].kind !== filter;
    });
    const current = Array.from(list.children);
    if (current.length !== nodes.length || nodes.some((node, index) => current[index] !== node)) {
      list.replaceChildren(...nodes);
    }
    empty.hidden = items.some(item => item.kind === filter);
    empty.textContent = syncing && !accountReady ? (lastError || (config.signedIn ? 'Loading saved items…' : 'Sign in to view your saved items.')) : filter === 'product' ? 'Your wishlist is empty.' : 'You haven’t saved any articles yet.';
    list.setAttribute('aria-busy', 'false');
  }

  function render() {
    const items = read();
    for (const button of document.querySelectorAll('[data-woofin-save]')) {
      const saved = items.some(item => item.id === button.dataset.id && item.kind === button.dataset.kind);
      button.setAttribute('aria-pressed', String(saved));
      if (button.hasAttribute('data-save-icon')) {
        const label = saved ? 'Remove article from saved' : 'Save article';
        button.setAttribute('aria-label', label);
        button.title = label;
        const saveLabel = button.querySelector('[data-save-label]');
        if (saveLabel) saveLabel.textContent = saved ? 'Saved' : 'Save';
      } else {
        button.textContent = saved ? 'Saved' : 'Save';
      }
    }
    for (const section of document.querySelectorAll('[data-woofin-saved]')) {
      renderSection(section, items);
    }
  }

  document.addEventListener('click', async event => {
    const button = event.target.closest('button');
    if (!button) return;
    const section = button.closest('[data-woofin-saved]');
    if (section && button.hasAttribute('data-filter')) {
      filters.set(section, button.dataset.filter);
      render();
      return;
    }
    if (!button.matches('[data-woofin-save], [data-woofin-remove]')) return;
    const { id, kind, title, url, image, excerpt } = button.dataset;
    if (syncing) {
      event.preventDefault();
      const item = { id, kind, title, url, image, excerpt };
      const action = button.hasAttribute('data-woofin-remove') || button.getAttribute('aria-pressed') === 'true' ? 'remove' : 'save';
      if (!config.signedIn) { loginFor(item); return; }
      button.disabled = true;
      try {
        await loadAccount();
        if (!accountReady) throw Error(lastError || 'Please sign in again to save items.');
        // Refresh identity and token before each write; account expiry must never appear successful.
        if (!await accountRequest()) { loginFor(item); return; }
        if (!await accountRequest({ action, item })) { loginFor(item); return; }
        status(action === 'save' ? 'Saved to your WOOFIN account.' : 'Removed from saved items.');
        render();
        if (section) section.querySelector('[data-filter][aria-pressed="true"]')?.focus();
      } catch (error) { status(error.message); }
      finally { button.disabled = false; }
      return;
    }
    const items = read();
    const remaining = items.filter(item => item.id !== id || item.kind !== kind);
    if (remaining.length === items.length && button.hasAttribute('data-woofin-save')) {
      remaining.unshift({ id, kind, title, url, image, excerpt });
    }
    try {
      localStorage.setItem(key, JSON.stringify(remaining));
    } catch {
      window.alert('Your browser couldn’t update saved items.');
      return;
    }
    render();
    if (section) section.querySelector('[data-filter][aria-pressed="true"]')?.focus();
  });
  window.addEventListener('storage', event => { if (!syncing && event.key === key) render(); });
  window.addEventListener('pageshow', () => { if (syncing) { accountItems = []; accountReady = false; render(); loadAccount(); } });
  window.addEventListener('focus', () => { if (syncing) loadAccount(); });
  document.addEventListener('shopify:section:load', render);
  render();
  if (syncing) loadAccount();
})();
